import React, { useEffect, useState } from 'react';
import { useTypedTranslation } from '@app/hooks';
import { Badge, Menu, Link, Checkbox } from '@material-ui/core';
import { IUserData, IMessages } from '@app/models';
import { basicService, notificationService } from '@app/services';
import { fromNow, genericErrorToast } from '@app/helpers';
import { PasslyButton, PasslyInfiniteScroll } from '..';
import Icon from '@app/components/Icon';
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import CloseIcon from '@material-ui/icons/Close';
import { PasslyTooltip } from '@app/componentsV2';

interface IProps {
    user: IUserData;
}

interface IMessageTableData {
    body: string;
    subject: string;
    created: string;
    id: string;
    important: boolean;
    isRead: boolean;
}

export const NotificationBell: React.SFC<IProps> = ({ user }) => {
    const { translation } = useTypedTranslation();

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [unreadMessageCount, setUnreadMessageCount] = useState(0);
    const [rows, setRows] = useState<IMessageTableData[]>([]);
    const [rowsCount, setRowsCount] = useState(0);
    const [resetScrollCount, setResetScrollCount] = useState(0);

    useEffect(() => {
        getUnreadCount();

        const timer = setInterval(function () {
            getUnreadCount();
        }, 300000); // 5 minutes

        return () => {
            clearInterval(timer);
        };
    }, []);

    const handleSuccessCount = (response: any) => {
        const cnt: number = response.data.value;

        setUnreadMessageCount(cnt);
    };

    const getUnreadCount = () => {
        if (user && user.username && !user.onboarding) {
            notificationService.getUnreadMessageCount(genericErrorToast, handleSuccessCount);
        }
    };

    const handleClick = (event: any) => {
        resetScroll();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSuccessMessages = (isReset: boolean) => (response: any) => {
        const results: IMessages[] = response.data.value;

        if (results) {
            let newRows = results.map((row: IMessages) => {
                const data: IMessageTableData = {
                    id: row.Id,
                    subject: row.Subject,
                    created: fromNow(row.Created),
                    body: fixBody(row.Body),
                    important: row.Important,
                    isRead: row.IsRead
                };

                return data;
            });

            if (isReset) {
                setRowsCount(response.data['@odata.count']);
            }

            if (newRows.length === 0) {
                setRows([]);
            } else if (isReset) {
                setRows(newRows);
            } else {
                setRows(rows.concat(newRows));
            }
        }
    };

    const fixBody = (body: string) => {
        body = body.replace(/\${homerealm}/gi, window.location.protocol + '//' + window.location.host);

        let html: any = document.createElement('span');
        html.innerHTML = body;

        let text: string = html.innerText || html.textContent;

        const length: number = 60;

        return text.length > length ? text.substring(0, length - 3) + '...' : text;
    };

    const goToSpecificMessage = (id: string) => () => {
        basicService.redirect('/profile/read-messages/details/' + id);
        handleClose();
    };

    const resetScroll = () => {
        setResetScrollCount(resetScrollCount + 1);
    };

    const clearRow = (id: string) => (e: any) => {
        const foundedElement = rows.findIndex((row) => row.id === id);

        markAsRead(id, resetScroll)(e);

        if (foundedElement !== -1) {
            rows.splice(foundedElement, 1);
            setRows(rows);
        }
    };

    const clearAll = () => {
        setRows([]);
        handleClose();
    };

    const getUnReadNotifications = () => {
        let jsonObject: any = {};
        jsonObject.Messages = [];

        rows.forEach(function (row) {
            if (!row.isRead) {
                jsonObject.Messages.push(row.id);
            }
        });

        return jsonObject;
    };

    const readAllMessages = () => {
        markAllAsRead(getUnReadNotifications());
    };

    const markAllAsRead = (jsonObject: any) => {
        notificationService.markAsRead(jsonObject, genericErrorToast, handleReadSuccess);
    };

    const handleReadSuccess = () => {
        const rowsToSet = rows.map((row) => {
            row.isRead = true;

            return row;
        });

        setRows(rowsToSet);
        getUnreadCount();
    };

    const markAsRead = (id: string, onSuccess?: Function) => (e: any) => {
        const rowsToSet = rows.map((row) => {
            if (row.id === id) {
                row.isRead = !row.isRead;
            }

            return row;
        });

        setRows(rowsToSet);
        notificationService.markMessageAsRead(id, { IsRead: !e.target.checked, Id: id }, genericErrorToast, () => {
            getUnreadCount();
            onSuccess && onSuccess();
        });
    };

    const goToAllMessages = () => {
        basicService.redirect('/profile/read-messages');
        handleClose();
    };

    const fetchData = (skipCount: number, isReset: boolean) => {
        notificationService.getUnreadMessages(skipCount, genericErrorToast, handleSuccessMessages(isReset));
    };

    const getMenuHeader = () => {
        const unreadNotifications = getUnReadNotifications();
        const haveUnread = !!unreadNotifications?.Messages?.length;

        return (
            <div className='notification__header'>
                {translation('NOTIFICATION_HEADER')}
                {rows.length && haveUnread ? (
                    <Link onClick={readAllMessages} id='readAllMessagesButton' className='notification__readAllButton'>
                        {translation('MARK_ALL_AS_READ')}
                    </Link>
                ) : rows.length && !haveUnread ? (
                    <Link onClick={clearAll} id='clearAllMessagesButton' className='notification__readAllButton'>
                        {translation('CLEAR_ALL')}
                    </Link>
                ) : null}
            </div>
        );
    };

    const getMenuItems = () => {
        return (
            <div className='notification__list'>
                <PasslyInfiniteScroll totalCount={rowsCount} currentCount={rows.length} onFetch={fetchData} resetLocal={resetScrollCount}>
                    {rows.length > 0 ? (
                        <>
                            {rows.map((row, index: number) => {
                                const title = row.isRead ? translation('MARK_UNREAD_MESSAGE') : translation('MARK_READ_MESSAGE');

                                return (
                                    <div key={index} className='notification__bellItem'>
                                        <PasslyTooltip title={title}>
                                            <Checkbox
                                                color='primary'
                                                className={`notification__bellItem-checkbox ${row.isRead ? 'isRead' : ''}`}
                                                id={`checkbox-${row.subject}`}
                                                checked={!row.isRead}
                                                onChange={markAsRead(row.id)}
                                                icon={<RadioButtonUncheckedIcon />}
                                                checkedIcon={<RadioButtonCheckedIcon />}
                                                disableRipple
                                            />
                                        </PasslyTooltip>
                                        <div onClick={goToSpecificMessage(row.id)} className='notification__bellItem-content'>
                                            <h4>
                                                <span>{row.subject}</span>
                                                {row.important && <span className='notification__importantFlag'>!</span>}
                                            </h4>
                                            <div className='notification__bellItem-description'>{row.body}</div>
                                            <div className='notification__bellItem-timestamp'>{row.created}</div>
                                        </div>
                                        <PasslyTooltip title={translation('NOTIFICATION_CLEAR_TOOLTIP')}>
                                            <button className='notification__bellItem-close' onClick={clearRow(row.id)}>
                                                <CloseIcon className='notification__bellItem-icon' />
                                            </button>
                                        </PasslyTooltip>
                                    </div>
                                );
                            })}
                        </>
                    ) : (
                        <li>
                            <div className='notification__empty-list'>
                                <Icon icon='bellIco' className='notification__icon-bell' />
                                <div className='notification__empty-list-message-1'>{translation('EMPTY_MESSAGE_LIST_1')}</div>
                                <div className='notification__empty-list-message-2'>{translation('EMPTY_MESSAGE_LIST_2')}</div>
                            </div>
                        </li>
                    )}
                </PasslyInfiniteScroll>
            </div>
        );
    };

    const getMenuFooter = () => {
        return (
            <div className='notification__footer'>
                <Link onClick={goToAllMessages} id='viewAllMessagesButton' className='notification__viewAllButton'>
                    {translation('VIEW_ALL_MESSAGES')}
                </Link>
            </div>
        );
    };

    return (
        <>
            <PasslyTooltip title={translation('NAVBAR_NOTIFICATIONS_TOOLTIP')}>
                <span>
                    <PasslyButton
                        id='profileIconButton'
                        className={`notification__trigger ${anchorEl ? 'navbar__content-active' : ''}`}
                        onClick={handleClick}
                    >
                        <Badge className='notification__badge' badgeContent={unreadMessageCount} color='error' overlap='circular'>
                            <Icon icon='bellIco' className='navbar__icon icon-notifications notification__icon' />
                        </Badge>
                    </PasslyButton>
                </span>
            </PasslyTooltip>
            <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
                className='notification__menu'
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                getContentAnchorEl={null}
            >
                {getMenuHeader()}
                {getMenuItems()}
                {getMenuFooter()}
            </Menu>
        </>
    );
};
